import { motion } from "motion/react";
import { Link } from "react-router";
import { useState } from "react";
import { ProjectModal } from "./ProjectModal";
import { Users, Target, ShieldCheck, Zap, Briefcase, Award } from "lucide-react";

const teamValues = [
  {
    icon: <Users size={32} />,
    title: "Client-Centric",
    description: "We don't just build software; we build partnerships. Your success is our success."
  },
  {
    icon: <Target size={32} />,
    title: "Impact Driven",
    description: "We focus on outcomes, not just outputs. Every line of code serves a business goal."
  },
  {
    icon: <ShieldCheck size={32} />,
    title: "Uncompromising Quality",
    description: "We adhere to the highest standards of engineering, security, and design."
  },
  {
    icon: <Zap size={32} />,
    title: "Agile Innovation",
    description: "We adapt quickly to change, embracing new technologies to keep you ahead."
  }
];

const stats = [
  { label: "Projects Delivered", value: "150+" },
  { label: "Client Retention", value: "98%" },
  { label: "Team Experts", value: "10+" },
  { label: "Years of Excellence", value: "08+" }
];

export function AboutPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="pt-20">
      {/* Hero Header */}
      <section className="bg-slate-900 text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-[#FD5750] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-blue-500 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#FD5750] font-bold tracking-widest uppercase mb-4 block">About Intelisine</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              More Than Just <span className="text-[#FD5750]">Code</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              We are a team of strategists, designers, and engineers dedicated to transforming businesses through digital innovation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story / Mission */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Bridging the Gap Between <span className="text-[#FD5750]">Strategy</span> and <span className="text-[#FD5750]">Execution</span>
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              Intelisine was founded on a simple premise: Traditional IT service providers often lack strategic insight, while management consultancies often lack technical execution capabilities.
            </p>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              We fill that void. We sit with you to understand your business deeply - your pain points, your market, and your goals. Then, we leverage cutting-edge technology to build solutions that don't just work, but drive real growth.
            </p>
            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-slate-700 font-semibold">
                <Briefcase className="text-[#FD5750]" /> Strategic Consulting
              </div>
              <div className="flex items-center gap-2 text-slate-700 font-semibold">
                <Award className="text-[#FD5750]" /> Technical Excellence
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Team Collaboration" 
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100 hidden md:block">
              <p className="text-4xl font-bold text-[#FD5750] mb-1">8+</p>
              <p className="text-slate-600 font-medium">Years of Innovation</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="text-4xl md:text-5xl font-bold text-[#FD5750] mb-2">{stat.value}</p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These principles guide every decision we make and every line of code we write.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 bg-slate-50 rounded-xl hover:bg-white hover:shadow-xl hover:scale-105 transition-all duration-300 border border-slate-100"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-[#FD5750] shadow-sm mb-6 mx-auto">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 text-center">{value.title}</h3>
                <p className="text-slate-600 text-center text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
             <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Something Extraordinary?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join the 20+ companies that have trusted Intelisine to transform their digital presence.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center justify-center px-8 py-4 bg-[#FD5750] text-white font-bold rounded-lg hover:bg-[#E04F49] transition-colors shadow-lg shadow-[#FD5750]/25 cursor-pointer"
          >
            Start Your Journey
          </button>
        </div>
      </section>

      <ProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
